import { RemoteAudioTrack, RemoteTrackPublication } from 'livekit-client';
import React, { forwardRef, useCallback, useEffect, useRef } from 'react';
import { TrackReference, useEnsureTrackRef } from "@livekit/components-react";
import { useMediaTrackBySourceOrName } from "@/components/call/hooks/use-media-track-by-source-or-name.ts";

/** @public */
export interface SpatialAudioTrackProps
  extends React.AudioHTMLAttributes<HTMLAudioElement> {
  trackRef?: TrackReference;
  onSubscriptionStatusChanged?: (subscribed: boolean) => void;
  volume?: number;
  muted?: boolean;
  position?: { x: number; y: number; z: number };
}

export const SpatialAudioTrack = forwardRef<
  HTMLAudioElement,
  SpatialAudioTrackProps
>(function SpatialAudioTrack(
  { trackRef, onSubscriptionStatusChanged, volume = 1, muted = false, position, ...props },
  ref
) {
  const trackReference = useEnsureTrackRef(trackRef);
  const mediaEl = useRef<HTMLAudioElement>(null);

  // Audio graph refs
  const audioCtx = useRef<AudioContext | null>(null);
  const sourceNode = useRef<MediaStreamAudioSourceNode | null>(null);
  const panner = useRef<PannerNode | null>(null);
  const gainNode = useRef<GainNode | null>(null);
  const attachedTrack = useRef<RemoteAudioTrack | null>(null);
  const isGraphInitialized = useRef(false);

  const lastPosition = useRef<{ x: number; y: number; z: number } | null>(null);

  React.useImperativeHandle(ref, () => mediaEl.current as HTMLAudioElement);

  const {
    elementProps,
    isSubscribed,
    track,
    publication: pub,
  } = useMediaTrackBySourceOrName(trackReference, {
    element: mediaEl,
    props,
  });

  React.useEffect(() => {
    onSubscriptionStatusChanged?.(!!isSubscribed);
  }, [isSubscribed, onSubscriptionStatusChanged]);

  // disable HTML audio playback (prevent double audio)
  React.useEffect(() => {
    if (mediaEl.current) {
      mediaEl.current.muted = true;
      mediaEl.current.volume = 0;
      mediaEl.current.autoplay = true;
      mediaEl.current.controls = false;
      mediaEl.current.srcObject = null;
    }
  }, []);


  // Initialize audio graph (runs ONLY on track change)
  useEffect(() => {
    const remoteTrack = trackReference?.publication?.track;
    if (!(remoteTrack instanceof RemoteAudioTrack)) return;
    const trackId = remoteTrack?.mediaStreamTrack?.id;
    const prevId = attachedTrack.current?.mediaStreamTrack?.id;

    if (trackId && trackId === prevId && isGraphInitialized.current) return;
    // if (attachedTrack.current === remoteTrack && isGraphInitialized.current) return;
    if (attachedTrack.current === remoteTrack) return;

    // Cleanup existing graph before creating new one
    if (isGraphInitialized.current) {
      sourceNode.current?.disconnect();
      panner.current?.disconnect();
      gainNode.current?.disconnect();
      if (audioCtx.current) {
        audioCtx.current.close().catch(console.warn);
      }
      isGraphInitialized.current = false;
    }

    attachedTrack.current = remoteTrack;
    if (pub instanceof RemoteTrackPublication) pub.setEnabled(!muted);

    // Initialize AudioContext with resume handling
    audioCtx.current = new AudioContext();

    if (audioCtx.current.state === 'suspended') {
      audioCtx.current.resume().catch(console.warn);
    }

    const stream = new MediaStream([remoteTrack.mediaStreamTrack]);
    if (sourceNode.current) {
      try {
        sourceNode.current.disconnect();
      } catch {}
      sourceNode.current = null;
    }
    sourceNode.current = audioCtx.current.createMediaStreamSource(stream);

    // Configure PannerNode for high-quality spatial audio
    panner.current = audioCtx.current.createPanner();
    panner.current.panningModel = 'HRTF';
    panner.current.distanceModel = 'inverse';
    panner.current.refDistance = 1;
    panner.current.rolloffFactor = 1;
    panner.current.channelCount = 2;
    panner.current.channelCountMode = 'clamped-max';
    panner.current.channelInterpretation = 'speakers';

    // Configure GainNode with smoothing
    gainNode.current = audioCtx.current.createGain();
    gainNode.current.channelCount = 2;
    gainNode.current.channelCountMode = 'explicit';

    // Build audio graph: source → panner → gain → destination
    sourceNode.current
      .connect(panner.current)
      .connect(gainNode.current)
      .connect(audioCtx.current.destination);

    // Apply initial position and volume
    if (position) applyPosition(position);
    applyGain(volume, muted);

    isGraphInitialized.current = true;

    return () => {
      sourceNode.current?.disconnect();
      panner.current?.disconnect();
      gainNode.current?.disconnect();
      if (audioCtx.current) {
        audioCtx.current.close().catch(console.warn);
      }
      attachedTrack.current = null;
      isGraphInitialized.current = false;
    };
  }, [trackReference, pub]);

  // Smooth gain/volume updates (does NOT recreate graph)
  const applyGain = useCallback((newVolume: number, isMuted: boolean) => {
    if (!gainNode.current || !audioCtx.current) return;

    const targetGain = isMuted || newVolume <= 0 ? 0 : newVolume;
    const ctx = audioCtx.current;

    // Use exponential ramp for click-free transitions
    gainNode.current.gain.cancelScheduledValues(ctx.currentTime);
    const t = ctx.currentTime;
    gainNode.current.gain.cancelScheduledValues(t);
    gainNode.current.gain.setValueAtTime(gainNode.current.gain.value, t);
    gainNode.current.gain.linearRampToValueAtTime(targetGain, t + 0.03);
  }, []);

  // Position updates with audio-rate scheduling
  const applyPosition = useCallback((pos: { x: number; y: number; z: number }) => {
    if (!panner.current || !audioCtx.current) return;

    const ctx = audioCtx.current;
    const t = ctx.currentTime;

    // Schedule parameter changes at audio rate to avoid glitches
    panner.current.positionX.linearRampToValueAtTime(pos.x, t + 0.05);
    panner.current.positionY.linearRampToValueAtTime(pos.y, t + 0.05);
    panner.current.positionZ.linearRampToValueAtTime(pos.z, t + 0.05);
  }, []);

  // Handle volume/mute changes
  useEffect(() => {
    applyGain(volume, muted);
    if (pub instanceof RemoteTrackPublication) {
      pub.setEnabled(!muted);
    }
  }, [volume, muted, pub, applyGain]);

  // Handle position prop changes
  useEffect(() => {
    if (position && isGraphInitialized.current) {
      applyPosition(position);
    }
  }, [position, applyPosition]);

  useEffect(() => {
    if (!attachedTrack.current || !pub) return;
    if (pub === undefined) {
      return;
    }
    if (pub instanceof RemoteTrackPublication) {
      const isEnabled = !muted;
      pub.setEnabled(isEnabled);

      if (gainNode.current && audioCtx.current) {
        const targetGain = isEnabled ? volume : 0;
        gainNode.current.gain.setTargetAtTime(targetGain, audioCtx.current.currentTime, 0.01);
      }
    }

  }, [muted, volume, pub]);

  return <audio ref={mediaEl} {...elementProps} muted />;
});
